import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  debounce,
  Grid,
  TextField,
} from '@mui/material';
import { useAuthContext } from 'src/components/Context/AuthContext/AuthContext';

import { url } from 'src/Helpers/utils/Constant';
import { IuserMemberList } from 'src/Types/module/invoice/invoiceTypes';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { HttpClient } from 'src/Helpers/http/http';
import Toaster from 'src/Toaster/Toaster';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { removeUndefinedAndNull } from 'src/common/removeUndefinedAndNull';
interface ISingleMember {
  user_member_id: number;
  user_member_company_name: string;
  user_member_representative_name: string;
}

const TransferMoneyReceipt = ({
  handleClose,
  setRefresh,
  moneyReceiptId,
}: any) => {
  const { user } = useAuthContext();
  const [companyName, seTCompanyName] = useState<string>('');
  const [userMember, setUserMember] = useState<IuserMemberList[]>([]);
  const [_isLoading, setIsLoading] = useState<Boolean>(false);

  const { handleSubmit, control, watch } = useForm();
  const { fields, append } = useFieldArray({
    control,
    name: 'items',
  });

  const [loading, setLoading] = useState(false);

  const items = watch('items');
  const totalItemAmount = items?.reduce(
    (sum, item) => sum + Number(item.collected_amount || 0),
    0
  );

  if (fields.length === 0) {
    append({ id: '', collected_amount: '', year: '' });
  }

  // --------------------------------------------------------------------
  // 🔥 FIXED useEffect — Only correct member fetching, nothing removed
  // --------------------------------------------------------------------
  useEffect(() => {
    const fetchMembers = async () => {
      if (!user?.user_admin_id) return;

      setIsLoading(true);

      try {
        const responses = await Promise.all([
          fetch(
            `${url}/api/atab/user-member/company-name/get-all/status-or-all/all?company_name=${encodeURIComponent(
              companyName || ''
            )}`
          ).then((r) => r.json()),
        ]);

        const result = responses[0];

        if (result?.data) {
          setUserMember(result.data); // ✔ Correct data set
        }
      } catch (error) {
        console.error('Error fetching user members:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMembers();
  }, [companyName, user?.user_admin_id]);

  // --------------------------------------------------------------------
  // SUBMIT LOGIC (kept exactly as you had)
  // --------------------------------------------------------------------
  const onSubmit = async (submitData: any) => {
    setLoading(true);

    submitData.items.forEach((item) => {
      item.amount = Number(item.amount);

      if (item.year.length) {
        item.year = item.year.map((year) => year.value).join('-');
      } else {
        item.year = `${moment().year()}`;
      }
    });

    try {
      const res: any = await HttpClient.patch(
        `${url}/api/atab/admin/money-receipt/old/${moneyReceiptId}`,
        removeUndefinedAndNull({
          ...submitData,
        })
      );

      if (res.success) {
        Toaster().fire({
          icon: 'success',
          title: res.message,
        });
        setLoading(false);
        handleClose();
        setRefresh((prv) => !prv);
      } else {
        setLoading(false);
      }
    } catch (err: any) {
      Toaster().fire({
        icon: 'error',
        title: `${err.response?.data?.message}`,
      });
      setLoading(false);
    }
  };

  const [member, setMember] = useState<ISingleMember | null>(null);

  const handelInputChange = debounce((value: string) => {
    seTCompanyName(value);
  }, 500);

  return (
    <>
      <Helmet>
        <title>Atab - Create Old Money Receipt</title>
      </Helmet>

      <Box component='form' onSubmit={handleSubmit(onSubmit)}>
        <Grid container padding={2} maxWidth={400}>
          <Grid item xs={12} md={12} lg={12}>
            {/* ⭐ ORIGINAL AUTOCOMPLETE — NOT TOUCHED ✨ */}
            <Controller
              control={control}
              name={`member_id`}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  value={member || null}
                  onChange={(_event, newValue) => {
                    field.onChange(newValue ? newValue.user_member_id : null);
                    setMember(newValue);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.user_member_id === value.user_member_id
                  }
                  loading={true}
                  id='controllable-states-demo'
                  options={userMember}
                  onInputChange={(_event, newInputValue) => {
                    handelInputChange(newInputValue);
                  }}
                  getOptionLabel={(option) => option.user_member_company_name}
                  renderTags={(value: readonly any[], getTagProps) =>
                    value.map((option: any, index: number) => (
                      <Chip
                        variant='filled'
                        label={option}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Select member'
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: <>{params.InputProps.endAdornment}</>,
                      }}
                    />
                  )}
                />
              )}
            />
          </Grid>

          <Box textAlign={'start'} mt={1}>
            {loading ? (
              <LoadingButton
                loading={loading}
                fullWidth
                color='secondary'
                disableElevation
                variant='contained'
              >
                Loading...
              </LoadingButton>
            ) : (
              <Button type='submit' variant='contained' disableElevation>
                Transfer Money Receipt
              </Button>
            )}
          </Box>
        </Grid>
      </Box>
    </>
  );
};

export default TransferMoneyReceipt;
